import { Component, OnInit, Input } from '@angular/core';
import { FormGroup, FormControl, Validators, ValidatorFn } from '@angular/forms';
import { ModalController, AlertController } from '@ionic/angular';
import * as HttpStatusCodes from 'http-status-codes';
import { Company, User } from 'src/app/classes';
import { PhoneValidator, phoneValidatorMode } from 'src/app/classes/validators/phone.validator';
import { SettingsmanagerService } from 'src/app/services/settingsmanager.service';
import { CountryPhone, PhoneNumberType } from 'src/app/pages/public/register/country-phone.model';
import { TranslateService } from '@ngx-translate/core';
import { WsmanagerService } from 'src/app/services/wsmanager.service';
import { HttpErrorResponse } from '@angular/common/http';
import { WidgetsmanagerService } from 'src/app/services/widgetsmanager.service';
import { forkJoin, Subscription } from 'rxjs';
import { EntityTypeEnum, ITranslation } from 'src/app/classes/ITranslation';

@Component({
  selector: 'app-editor-company',
  templateUrl: './editor-company.component.html',
  styleUrls: ['./editor-company.component.scss']
})
export class EditorCompanyComponent implements OnInit {
  @Input('companyId')
  public companyId: number;

  public company: Company;

  @Input()
  isReadOnly = false;

  public frmInfoCompany: FormGroup;
  public validation_messages: any;
  public countries: CountryPhone[];
  public nationList: Array<string[]>;
  public isLoading: boolean;
  public users: User[];

  // supporto modale tranduzioni
  public showTranslationsModal = false;
  public translations: ITranslation[] = [];
  public subscriptions: Subscription[] = [];
  public entityType: EntityTypeEnum = EntityTypeEnum.Organization;
  public entityId = -1;

  constructor(
    private _alertController: AlertController,
    private _settingsManager: SettingsmanagerService,
    private _modalController: ModalController,
    private _translate: TranslateService,
    private _ws: WsmanagerService,
    private _widgetsManager: WidgetsmanagerService
  ) {
    this.isLoading = false;
    this.countries = [new CountryPhone('IT', 'Italy', PhoneNumberType.FIXED)];
    this.nationList = [['IT', 'Italy']];
  }

  /*******************************************************************
    eventi componente
  *******************************************************************/

  ionViewWillEnter() {
    this.populateCountries();
  }

  ngOnInit() {
    const countryBusiness = new FormControl(this.countries[0], Validators.required);
    const phoneBusiness = new FormControl(null, [Validators.required, PhoneValidator.validCountryPhone(countryBusiness, phoneValidatorMode.ALL)]);
    const country_phone_group_business = new FormGroup({
      country: countryBusiness,
      phone: phoneBusiness
    });
    // init form
    this.frmInfoCompany = new FormGroup(
      {
        vatCode: new FormControl(null, [Validators.required, Validators.pattern('[0-9]{11}')]),
        taxCode: new FormControl(null, [Validators.required]),
        businessName: new FormControl(null, [Validators.required]),
        paymentUniqueCode: new FormControl(null, [Validators.required]),
        address: new FormControl(null, [Validators.required]),
        cap: new FormControl(null, [Validators.required]),
        phone: country_phone_group_business,
        emailPec: new FormControl(null, [Validators.required, Validators.pattern('^[\\s]*[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+.[a-zA-Z0-9-.]+[\\s]*$')]),
        city: new FormControl(null, [Validators.required]),
        prov: new FormControl(null),
        nation_iso: new FormControl(null, [Validators.required]),
        email: new FormControl(null, [Validators.required, Validators.pattern('^[\\s]*[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+.[a-zA-Z0-9-.]+[\\s]*$')])
      },
      this.provMandatoryIfItalianNationValidator
    );
    if (this.companyId) {
      this.frmInfoCompany.addControl('id', new FormControl(this.companyId, [Validators.required]));
      this.loadDetail(this.companyId);
    }
    if (this.isReadOnly) {
      this.frmInfoCompany.disable();
    }

    // mex errore
    this.validation_messages = {
      vatCode: [
        { type: 'required', message: 'Field is required.' },
        { type: 'pattern', message: 'Please enter a valid vat code.' }
      ],
      taxCode: [{ type: 'required', message: 'Field is required.' }],
      businessName: [{ type: 'required', message: 'Field is required.' }],
      paymentUniqueCode: [{ type: 'required', message: 'Field is required.' }],
      address: [{ type: 'required', message: 'Field is required.' }],
      cap: [{ type: 'required', message: 'Field is required.' }],
      city: [{ type: 'required', message: 'Field is required.' }],
      prov: [
        {
          type: 'mandatoryProvIfItalian',
          message: 'Field is required when nation is Italy.'
        },
        {
          type: 'maxLength',
          message: 'This field must be at most 2 characters long.'
        }
      ],
      nation_iso: [{ type: 'required', message: 'Field is required.' }],
      phone: [
        { type: 'required', message: 'Phone is required.' },
        {
          type: 'validCountryPhone',
          message: 'The phone is incorrect for the selected country.'
        }
      ],
      email: [
        { type: 'required', message: 'Field is required.' },
        { type: 'pattern', message: 'Please enter a valid email.' }
      ],
      emailPec: [
        { type: 'required', message: 'Email is required.' },
        { type: 'email', message: 'Please enter a valid email.' }
      ]
    };
  }

  private provMandatoryIfItalianNationValidator: ValidatorFn = (frm: FormGroup) => {
    if (frm.controls.nation_iso.value === 'IT' && !frm.controls.prov.value) {
      frm.controls.prov.setErrors({ mandatoryProvIfItalian: true });
    } else {
      frm.controls.prov.setErrors(null);
    }
    return null;
  };

  public loadDetail(id: number) {
    this.isLoading = true;
    forkJoin(this._ws.companyGetDetails(id), this._ws.companyGetUsers(id)).subscribe(
      (itemList) => {
        this.users = itemList[1];
        const item = itemList[0];

        // init valori in form ---> telefono
        let countryPhoneSelected = this.countries.find((x) => x.iso === 'IT');
        if (item.phone) {
          for (let i = 0; i < this.countries.length; i++) {
            const val = this.countries[i];
            if (item.phone.startsWith(val.code)) {
              countryPhoneSelected = val;
              break;
            }
          }
        }

        this.entityId = +item.id;
        this.loadTranslationsByEntity();
        this.company = item;
        // init valori form
        this.frmInfoCompany.setValue({
          id: item.id,
          vatCode: item.vatCode,
          taxCode: item.taxCode,
          businessName: item.businessName,
          paymentUniqueCode: item.paymentUniqueCode,
          address: item.address,
          cap: item.cap,
          emailPec: item.emailPec,
          email: item.email,
          city: item.city,
          prov: item.prov,
          nation_iso: item.nation_iso,
          phone: {
            country: countryPhoneSelected,
            phone: item.phone.replace(countryPhoneSelected.code, '')
          }
        });

        this.isLoading = false;
      },
      (err: HttpErrorResponse) => {
        this.isLoading = false;
      }
    );
  }

  /***********************************
    azioni
  ***********************************/

  public save() {
    // check
    if (this.frmInfoCompany.invalid) {
      this._widgetsManager.showToastMessage('danger', this._translate.instant('business_account.error_save'), 2000);
      return;
    }

    // preparazione info per invio
    const parmsToBeSent: Company = new Company(this.frmInfoCompany.value);
    parmsToBeSent.phone = this.frmInfoCompany.value.phone.country.code + this.frmInfoCompany.value.phone.phone;

    // invio
    const obs = parmsToBeSent.id ? this._ws.companyEdit(parmsToBeSent.id, parmsToBeSent) : this._ws.companyCreate(parmsToBeSent);
    this.isLoading = true;
    obs.subscribe(
      async () => {
        this.isLoading = false;
        this._modalController.dismiss(true);
      },
      (err: HttpErrorResponse) => {
        this.isLoading = false;
        const mex = err.status === HttpStatusCodes.FORBIDDEN ? 'business_account.error_save_forbidden' : 'business_account.error_save_generic';
        this._widgetsManager.showToastMessage('danger', this._translate.instant(mex), 2000);
      }
    );
  }

  public async delete() {
    const alert = await this._alertController.create({
      header: this._translate.instant('business_account.confirm_delete', {
        businessName: this.frmInfoCompany.value.businessName
      }),
      message: this._translate.instant('business_account.confirm_delete_description'),
      cssClass: 'alertConfirmClass',
      buttons: [
        {
          text: this._translate.instant('Dismiss'),
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => {}
        },
        {
          text: this._translate.instant('Yes'),
          handler: () => {
            this.isLoading = true;
            this._ws.companyDelete(this.frmInfoCompany.value.id).subscribe(
              () => {
                this.isLoading = false;
                this._modalController.dismiss(true);
              },
              (err: HttpErrorResponse) => {
                this.isLoading = false;
                const mex = err.status === HttpStatusCodes.FORBIDDEN ? 'business_account.error_delete_forbidden' : 'business_account.error_delete_generic';
                this._widgetsManager.showToastMessage('danger', this._translate.instant(mex), 2000);
              }
            );
          }
        }
      ]
    });
    await alert.present();
  }

  refreshContent(refreshEvent: { method: 'CREATE' | 'DELETE' | 'EDIT'; translation: ITranslation }) {
    const lang = this._settingsManager.getLanguageSetting();
    const { method, translation } = refreshEvent;
    switch (method) {
      case 'CREATE':
        this.translations.push(translation);
        this.translations = [...this.translations];
        break;
      case 'DELETE':
        this.translations = this.translations.filter((t) => t.id !== translation.id);
        break;
      case 'EDIT': {
        const index = this.translations.findIndex((t) => t.id === translation.id);
        this.translations[index] = translation;
        break;
      }
    }
    if (translation.locale === lang) {
      if (translation.value === '') {
        this.frmInfoCompany.get(translation.field).setValue(this.company[translation.field]);
      } else {
        this.frmInfoCompany.get(translation.field).setValue(translation.value);
      }
    }
  }

  changeEntityLanguage(language: string) {
    this._settingsManager.setLanguageSetting(language);
    const fieldsToReset = ['businessName', 'address', 'city'];
    fieldsToReset.forEach((field) => {
      if (this.frmInfoCompany.get(field)) {
        this.frmInfoCompany.get(field).setValue(this.company[field]);
      }
    });
    const localizedtranslations = this.translations.filter((t) => t.locale === language);
    localizedtranslations.forEach((lt) => {
      this.frmInfoCompany.get(lt.field).setValue(lt.value);
    });
  }

  loadTranslationsByEntity() {
    this.subscriptions.push(
      this._ws.fetchTranslations(this.entityType, this.entityId).subscribe(
        (response) => {
          console.log('@@@@@ translations by entity');
          this.translations = response;
        },
        (err) => {
          console.error('Error fetching translations:', err);
        },
        () => {
          console.log('Completed fetching translations.');
        }
      )
    );
  }

  public cancel() { this._modalController.dismiss(); }

  private populateCountries() {
    for (const item of this._settingsManager.getCountriesList()) {
      const val = new CountryPhone(item[0], item[1]);
      if (item[0] !== 'IT') {
        this.countries.push(val);
        this.nationList.push([item[0], item[1]]);
      }
    }
  }
}
